import { Injectable } from '@angular/core';
import { map, take } from 'rxjs/operators';
import { ListItemModel } from 'src/util/angular/ng-material-helpers';
import { UserViewModel } from 'src/view-models/user-view-model';
import {
  maintenancePermissions,
  MaintenancePermissionsService,
} from './authorization/maintenance/maintenance-permissions.service';
import { AirCarrierHttpClient } from './flight-log-api/air-carriers/air-carriers-client.service';
import { UserRole } from './flight-log-api/shared/shared-client.model';

interface ListItemModelWithPermissions extends ListItemModel {
  isExternal?: boolean;
  hasPermissions?: (() => boolean) | undefined;
  children?: ListItemModelWithPermissions[];
}

interface SideNavMenuSection {
  header: string | null;
  menuItems: ListItemModelWithPermissions[];
  headerOnly?: boolean;
}

export interface SideNavMenu {
  sections: SideNavMenuSection[];
}

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  constructor(
    private _airCarriers: AirCarrierHttpClient,
    private _maintenancePermissions: MaintenancePermissionsService,
  ) {}

  public sideNavMenu(user: UserViewModel | undefined) {
    const menu: SideNavMenu = { sections: [] };
    if (user) {
      let flightLogMenuSection: SideNavMenuSection;
      if (user.isFlightLogUser) {
        const flightLogHeaderSection = {
          header: 'Flight Log',
          menuItems: [],
          headerOnly: true,
        };
        menu.sections.push(flightLogHeaderSection);
        flightLogMenuSection = {
          header: null,
          menuItems: [
            { title: 'Home', icon: 'home', route: '/' },
            { title: 'My Dashboard', icon: 'web', route: 'dashboards/pilot' },
            { title: 'My Flights', icon: 'flight_takeoff', route: 'flight-logs' },
            {
              title: 'Reports',
              icon: 'slow_motion_video',
              route: 'reports',
            },
            {
              title: 'Maintenance',
              icon: 'build',
              route: 'maintenance',
              children: [
                {
                  title: 'MX Admin',
                  icon: 'supervisor_account',
                  route: 'maintenance/parameters',
                  hasPermissions: () =>
                    this._maintenancePermissions.hasPermission(
                      maintenancePermissions.showMaintenanceAdminLink,
                    ),
                },
              ],
            },
            {
              title: 'Duty On',
              icon: 'history',
              route: 'duty-logs/duty-on',
            },
            {
              title: 'Duty Off',
              icon: 'history',
              route: 'duty-logs/duty-off',
            },
            {
              title: 'Duty History',
              icon: 'history',
              route: 'duty-logs',
            },
            {
              title: ' GoldenHour',
              icon: 'open_in_new',
              route: 'https://gh.goldenhourlive.com/admin/login.jsp',
              isExternal: true,
              hasPermissions: () => user.flightLogFeatures.showSidebarGoldenHourLink,
            },
          ],
        };
        menu.sections.push(flightLogMenuSection);
      }
      if (user.isPrdUser) {
        const accessibleAirCarriers$ = this._airCarriers
          .getAll()
          .pipe(map((res) => res.results))
          .pipe(
            map((airCarriers) =>
              airCarriers.filter((airCarrier) =>
                user.accessibleAirCarriers.find((aac) => aac.id === airCarrier.id),
              ),
            ),
            map((airCarriers) =>
              airCarriers.sort((a, b) => a.abbreviation.localeCompare(b.abbreviation)),
            ),
          );

        accessibleAirCarriers$.pipe(take(1)).subscribe((airCarriers) => {
          if (airCarriers.length > 0) {
            const prdHeaderSection = {
              header: 'PRD',
              menuItems: [],
              headerOnly: true,
            };
            menu.sections.push(prdHeaderSection);
          }
          airCarriers.forEach((ac) => {
            const airCarrierMenuSection: SideNavMenuSection = {
              header: ac.abbreviation,
              menuItems: [
                {
                  title: 'Pilots',
                  icon: 'person',
                  route: `/prd/${ac.abbreviation}/pilots`,
                },
                {
                  title: 'Transmission Runs',
                  icon: 'timelapse',
                  route: `/prd/${ac.abbreviation}/transmission-runs`,
                },
                {
                  title: 'User Activity',
                  icon: 'history',
                  route: `/prd/${ac.abbreviation}/user-activity-reports`,
                  hasPermissions: () => user.userRoles.includes(UserRole.PrdAdmin),
                },
              ],
            };
            menu.sections.push(airCarrierMenuSection);
          });
          const prdUserPermissions = {
            header: null,
            menuItems: [
              {
                title: 'Permissions',
                icon: 'supervisor_account',
                route: '/prd/permissions',
                hasPermissions: () => user.userRoles.includes(UserRole.PrdAdmin),
              },
            ],
          };
          menu.sections.push(prdUserPermissions);
        });
      }
    }
    return menu;
  }
}
